// Angular
import { Injectable, inject } from '@angular/core';

// Third party
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';

// Local library
import {
  ICounterModel,
  IGetEventsDetailsResponse
} from '@amwager/util-ts';
import { WagerFacade } from '../+state/wager.facade';

/**
 * Bet summary totals — pre-formatted to 2 decimals
 */
export interface IBetSummaryTotals {
  stackTodaySum: string;
  betTodaySum: string;
  wonTodaySum: string;
  profitLossSum: string;
  activeBetsSum: string;
  realTimePlSum: string;
}

const EMPTY_TOTALS: IBetSummaryTotals = {
  stackTodaySum: '0.00',
  betTodaySum: '0.00',
  wonTodaySum: '0.00',
  profitLossSum: '0.00',
  activeBetsSum: '0.00',
  realTimePlSum: '0.00'
};

/**
 * Bet Summary header panel state — shared source of truth for
 * visibility and totals, consumed by the header + any surface
 * that needs to open/close the panel.
 */
@Injectable()
export class BetSummaryService {
  private readonly wagerFacade = inject(WagerFacade);

  private readonly _showBetSummary$ = new BehaviorSubject<boolean>(false);
  private readonly _totals$ = new BehaviorSubject<IBetSummaryTotals>(EMPTY_TOTALS);

  showBetSummary$: Observable<boolean> = this._showBetSummary$.asObservable();
  totals$: Observable<IBetSummaryTotals> = this._totals$.asObservable();

  private countersSub?: Subscription;
  private selectedEventSub?: Subscription;

  /** Sync open/closed snapshot */
  get isOpen(): boolean {
    return this._showBetSummary$.getValue();
  }

  /**
   * Open the panel and fetch fresh counters
   * @returns {void}
   */
  open(): void {
    this._showBetSummary$.next(true);
    this.refresh();
  }

  /**
   * Close the panel and unsubscribe pending counters
   * @returns {void}
   */
  close(): void {
    this._showBetSummary$.next(false);
    this.teardownSubs();
  }

  /**
   * Toggle open/close state
   * @returns {void}
   */
  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  /**
   * Re-fetch counters for the current run and republish totals
   * @returns {void}
   */
  refresh(): void {
    this.teardownSubs();

    this.selectedEventSub = this.wagerFacade.getSelectedEventTrack$()
      .pipe(take(1))
      .subscribe((selectedEventTrack?: IGetEventsDetailsResponse) => {
        const runId = selectedEventTrack?.ToteResponse?.RunId;

        if (!runId) {
          this._totals$.next(EMPTY_TOTALS);
          return;
        }

        this.wagerFacade.getCountersAPI(String(runId), 'BetSummaryService:refresh');

        this.countersSub = this.wagerFacade.getCounters$()
          .pipe(take(2))
          .subscribe((counter?: ICounterModel) => {
            this._totals$.next(
              counter ? this.buildTotals(counter) : EMPTY_TOTALS
            );
          });
      });
  }

  /**
   * Reset panel state and clear totals (called on logout)
   * @returns {void}
   */
  reset(): void {
    this.close();
    this._totals$.next(EMPTY_TOTALS);
  }

  /**
   * Map ICounterModel payload to formatted totals
   * @param {ICounterModel} counter raw response payload
   * @returns {IBetSummaryTotals} formatted totals
   */
  private buildTotals(counter: ICounterModel): IBetSummaryTotals {
    const stacked = counter?.TotalStacked ?? 0;
    const active = counter?.TotalActive ?? 0;
    const closed = counter?.TotalClosed ?? 0;
    const earnings = counter?.WinningsClosed ?? 0;

    return {
      stackTodaySum: stacked.toFixed(2),
      betTodaySum: (active + closed).toFixed(2),
      wonTodaySum: earnings.toFixed(2),
      profitLossSum: (earnings - closed - active).toFixed(2),
      activeBetsSum: active.toFixed(2),
      realTimePlSum: (earnings - closed).toFixed(2)
    };
  }

  /**
   * Unsubscribe any inflight counters/event subscriptions
   * @returns {void}
   */
  private teardownSubs(): void {
    this.selectedEventSub?.unsubscribe();
    this.selectedEventSub = undefined;
    this.countersSub?.unsubscribe();
    this.countersSub = undefined;
  }
}
